import { useState } from 'react';
import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconAdd from '@/icons/Add.svg';
import { computeDuplicateActionNumber } from '@/models/actionType';
import { useIsUpdatingWorkshop } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import useViewPort from '@/utils/useViewport';

import Loader from '../Loader';

import { ActionCard } from './ActionCard/ActionCard';
import { ActionTypeSearchSelect } from './ActionTypeSearchSelect';
import { CreateActionDialogs } from './CreateActionDialogs';

export const ActionsList = () => {
  const { isMobile } = useViewPort();

  const { article, view: articleView } = useArticleContext();

  const actions = article.snapshot.articleActions;
  const previousActions = article.previousSnapshot?.articleActions ?? [];

  const deletedActions = previousActions.filter(
    (prevAction) => !actions.some((action) => prevAction.stableId === action.stableId)
  );

  const [showAddActionOptions, setShowAddActionOptions] = useState(false);

  const isUpdatingWorkshop = useIsUpdatingWorkshop(article.id);

  return (
    <Stack gap="0.25rem">
      {articleView.services.dispatchBeforeActions && (
        <p className="label-100-medium text-primary">
          <Trans id="article.form.actions.label">Actions</Trans>
        </p>
      )}
      {articleView.services.dispatchBeforeActions && isUpdatingWorkshop ? (
        <Loader style={{ marginTop: '0.25rem' }} />
      ) : (
        <>
          <Stack gap="1rem">
            {[...previousActions, ...actions].length === 0 ? (
              <InlineText color="secondary">
                <Trans id="article.form.actions.table.no-actions-added">No actions added yet</Trans>
              </InlineText>
            ) : (
              <>
                {deletedActions.map((previousAction) => (
                  <ActionCard key={previousAction.id} previousAction={previousAction} />
                ))}
                {actions.map((action) => (
                  <ActionCard
                    key={action.id}
                    action={action}
                    previousAction={previousActions.find(
                      (previousAction) => action.stableId === previousAction.stableId
                    )}
                    duplicateActionNumber={computeDuplicateActionNumber(action, actions)}
                  />
                ))}
              </>
            )}

            {articleView.services.actions.canAddAction &&
              (isMobile ? (
                <>
                  <AddActionButton setShowAddActionsOptions={setShowAddActionOptions} />
                  <CreateActionDialogs
                    isOpen={showAddActionOptions}
                    setIsOpen={setShowAddActionOptions}
                  />
                </>
              ) : (
                <>
                  {!showAddActionOptions ? (
                    <AddActionButton setShowAddActionsOptions={setShowAddActionOptions} />
                  ) : (
                    <ActionTypeSearchSelect onBlur={() => setShowAddActionOptions(false)} />
                  )}
                </>
              ))}
          </Stack>

          <ActionsListErrors />
        </>
      )}
    </Stack>
  );
};

const AddActionButton = ({
  setShowAddActionsOptions,
}: {
  setShowAddActionsOptions: (show: boolean) => void;
}) => {
  return (
    <Button
      size="medium"
      variant="secondary"
      style={{ alignSelf: 'flex-start' }}
      onPress={() => setShowAddActionsOptions(true)}
      dataTrackingId="article.form.actions.add"
    >
      <IconAdd />
      <Trans id="actions-table.column.footer.add-action">Add an action</Trans>
    </Button>
  );
};

const ActionsListErrors = () => {
  const { errors, iam } = useArticleContext();

  const tableErrors = [];

  if (errors.services?.actions?.noActions)
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.no-actions">
        Please add at least one action.
      </Trans>
    );

  if (errors.services?.actions?.actions?.some((action) => action.missingDescription)) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.missing-description">
        Please add a description for each action.
      </Trans>
    );
  }

  if (errors.services?.actions?.actions?.some((action) => action.missingPhotos)) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.missing-photos">
        Please add at least one defect photo per action.
      </Trans>
    );
  }

  if (errors.services?.actions?.actions?.some((action) => action.missingRefashionPhoto)) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.missing-refashion-photos">
        Please add at least one defect photo for each action having a repair bonus.
      </Trans>
    );
  }

  if (errors.services?.actions?.actions?.some((action) => action.missingWorkshopPrice)) {
    tableErrors.push(
      iam.hasOnlyWorkshopScopeOnArticle ? (
        <Trans id="article.form.actions.table.errors.missing-workshop-price.workshop">
          Please add the price for each action.
        </Trans>
      ) : (
        <Trans id="article.form.actions.table.errors.missing-workshop-price">
          Please add the internal cost for each action.
        </Trans>
      )
    );
  }

  if (errors.services?.actions?.actions?.some((action) => action.missingOrganizationPrice)) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.missing-organization-price">
        Please add the client price for each action.
      </Trans>
    );
  }

  if (errors.services?.actions?.actions?.some((action) => action.missingContext)) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.missing-context">
        Please add context for each action.
      </Trans>
    );
  }

  if (
    errors.services?.actions?.actions?.some(
      (action) => action.workshopPriceUpdateInProgress || action.organizationPriceUpdateInProgress
    )
  ) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.price-update-in-progress">
        Please save or cancel the price updates for each action.
      </Trans>
    );
  }

  if (
    errors.services?.actions?.actions?.some((action) =>
      action.spareParts?.some((sparePart) => sparePart.missingWorkshopPrice)
    )
  ) {
    tableErrors.push(
      iam.hasOnlyWorkshopScopeOnArticle ? (
        <Trans id="article.form.actions.table.spare-parts.errors.missing-workshop-price.workshop">
          Please add the price for each spare part.
        </Trans>
      ) : (
        <Trans id="article.form.actions.table.spare-parts.errors.missing-workshop-price">
          Please add the internal cost for each spare part.
        </Trans>
      )
    );
  }

  if (
    errors.services?.actions?.actions?.some((action) =>
      action.spareParts?.some((sparePart) => sparePart.missingOrganizationPrice)
    )
  ) {
    tableErrors.push(
      <Trans id="article.form.actions.table.spare-parts.errors.missing-organization-price">
        Please add the client price for each spare part.
      </Trans>
    );
  }

  if (
    errors.services?.actions?.actions?.some((action) =>
      action.spareParts?.some(
        (sparePart) =>
          sparePart.workshopPriceUpdateInProgress || sparePart.organizationPriceUpdateInProgress
      )
    )
  ) {
    tableErrors.push(
      <Trans id="article.form.actions.table.spare-parts.errors.price-update-in-progress">
        Please save or cancel the price updates for each spare part.
      </Trans>
    );
  }

  if (
    errors.services?.actions?.actions?.some((action) =>
      action.additionalInfo?.some((additionalInfo) => additionalInfo.missingValue)
    )
  ) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.additional-info-missing">
        Please complete all the required fields for each action.
      </Trans>
    );
  }

  if (
    errors.services?.actions?.actions?.some(
      (action) => !!action.spareParts?.some((sparePart) => !!sparePart.quantityIsNull)
    )
  ) {
    tableErrors.push(
      <Trans id="article.form.actions.table.errors.spare-part-quantity-is-null">
        Please make sure that every spare part has a quantity other than zero.
      </Trans>
    );
  }

  if (tableErrors.length === 0) {
    return null;
  }

  return (
    <Stack gap="0.25rem" style={{ paddingTop: '0.25rem' }}>
      {tableErrors.map((error, index) => (
        <Message key={index} type="error">
          {error}
        </Message>
      ))}
    </Stack>
  );
};
